import type { DateTime } from "luxon";
import { DAYS_PER_WEEK, type WeekMeta } from "../../../pages/Profile/v2/WeeklySchedule/weekScheduleCellResolver";

/**
 * The start of the visible week containing `date`, or `undefined` when no week covers it.
 *
 * Ranges are week-granular on purpose: a block spans exactly seven columns, so a range ending
 * mid-week has no representation in the sheet.
 */
export const findWeekStartContaining = (weeks: WeekMeta[], date: DateTime): DateTime | undefined =>
	weeks.find((week) => date >= week.weekStart && date < week.weekStart.plus({ days: DAYS_PER_WEEK }))?.weekStart;

/**
 * Snap a picked date onto an exportable week start: the week that contains it, or the nearest end of
 * the available range when the pick falls outside it entirely.
 */
export const snapToAvailableWeekStart = (weeks: WeekMeta[], date: DateTime): DateTime | undefined => {
	const containing = findWeekStartContaining(weeks, date);
	if (containing) return containing;

	const first = weeks.at(0)?.weekStart;
	const last = weeks.at(-1)?.weekStart;
	if (!first || !last) return undefined;
	return date < first ? first : last;
};

/**
 * The weekdays that open and close a visible week, on the app's `weekdayISO` scale (0=Sun … 6=Sat),
 * taken from the weeks themselves rather than assumed.
 *
 * Sunday-aligned weeks give `{ start: 0, end: 6 }` — Sunday through Saturday. A department viewing
 * weeks from another day gets that day and its eve instead, so the range pickers can only ever offer
 * boundaries that a real week has.
 */
export const getWeekBoundaryWeekdays = (weeks: WeekMeta[]): { start: number; end: number } | undefined => {
	const first = weeks.at(0)?.weekStart;
	if (!first) return undefined;
	return { start: first.weekdayISO, end: first.plus({ days: DAYS_PER_WEEK - 1 }).weekdayISO };
};

/**
 * Weeks of history the range opens with. A profile can hold years of past weeks, and an export of
 * all of them is almost never what someone wants — recent context plus the schedule ahead is.
 */
export const DEFAULT_PAST_WEEKS = 2;

/**
 * Where the range starts before anyone touches it: `pastWeeks` before the week containing `today`,
 * never earlier than the first week available.
 *
 * Falls back to the earliest week when today sits outside the weeks entirely, which happens for a
 * profile whose schedule has already ended.
 */
export const resolveDefaultRangeStart = (weeks: WeekMeta[], today: DateTime, pastWeeks: number): DateTime | undefined => {
	const earliest = weeks.at(0)?.weekStart;
	if (!earliest) return undefined;

	const currentWeekStart = findWeekStartContaining(weeks, today) ?? earliest;
	const target = currentWeekStart.minus({ weeks: pastWeeks });
	return target < earliest ? earliest : target;
};

/** The weeks whose start falls within the inclusive `[from, to]` week-start range. */
export const filterWeeksToRange = (weeks: WeekMeta[], from: DateTime | undefined, to: DateTime | undefined): WeekMeta[] => {
	if (!from || !to) return weeks;
	return weeks.filter((week) => week.weekStart >= from && week.weekStart <= to);
};
